
import GameAbstract from '../core/GameAbstract';
import Template from 'frontools/data/Template';
import { autobind } from 'frontools/decorators/Autobind';
import ComponentAbstract from '../core/components/ComponentAbstract';
import DisplayUtils from 'frontools/utils/DisplayUtils';
import { Assets } from '../core/Assets';
import NumberUtils from 'frontools/utils/NumberUtils';
import { Explosion } from './Explosion';
import Context from 'frontools/core/Context';

export class Level extends ComponentAbstract {

    private _level                      : PIXI.Container                = new PIXI.Container();
    private _char                       : PIXI.Container                = new PIXI.Container();
    private _biteSource             : PIXI.Container                = new PIXI.Container();
    private _mask                       : PIXI.Sprite                   = new PIXI.Sprite();
    private _grid                       : PIXI.Container                = new PIXI.Container();
    private _bites                      : PIXI.Container                = new PIXI.Container();
    private _explosions                 : PIXI.Container                = new PIXI.Container();
    private _face                       : PIXI.extras.AnimatedSprite;
    private _context                    : Context                       = Context.getInstance();
    private _stage                      : PIXI.Container;
    private _renderer                   : PIXI.SystemRenderer;
    private _cells                      : PIXI.Container[]              = [];
    private _cellsTotal                 : number;
    private _progress                   : number                        = 0;
    private _emotionTimeout             : number;
    private _emotionState               : number                        = 0;
    private _emotions                   : number[]                      = [
        1, 7, 16, 23
    ];
    

    
    private _width                      : number;
    private _height                     : number;

    public static CLICK                 : string                        = 'click';
    public static TAP                   : string                        = 'tap';
    public static COMPLETE              : string                        = 'complete';
    public static MOUSE_UP_OUTSIDE      : string                        = 'mouseupoutside';
    public static HIT                   : string                        = 'hit';

    constructor(private _config: ILevelConfig) {
        super();
        
        this._config = this._config;
        this._renderer = GameAbstract.instance.renderer;
        this._stage = GameAbstract.instance.stage;
        this._width = Assets.sprites[this._config.character].width;
        this._height = Assets.sprites[this._config.character].height;

        this._char.addChild(Assets.sprites[this._config.character]);

        // Face positioning
        this._face = Assets.getSpriteFromJson('face') as PIXI.extras.AnimatedSprite;
        this._face.pivot.x = this._face.width / 2;
        this._face.pivot.y = this._face.height / 2;
        this._face.x = this._width / 2 + this._config.faceOffsetX;// + 100;
        this._face.y = this._config.faceOffsetY;

        this._face.animationSpeed = .3;
        this._face.onFrameChange = this._onFrameChange.bind(this);
        
        // transform origin "like"        
        this._level.addChild(this._char, this._mask, this._face, this._explosions, this._grid);
        this._generateGrid();
        this._prepareBites();

        this._stage.addChildAt(this._level, 1);
    };


    public get container(): PIXI.Container {
        return this._level;
    };


    public get config(): ILevelConfig {
        return this._config;
    };


    private _changeEmotion() {
        if (this._progress === 0) {
            var state = 0;
        } else if (this._progress > 0 && this._progress < 50) {
            var state = 1;
        } else if (this._progress >= 50 && this._progress < 75) {
            var state = 2;
        } else {
            var state = 3;
        }

        if (state > this._emotionState) {
            this._emotionState = state;
            this._face.play();
        }
    };

    private _onFrameChange(frameindex: number) {
        if (this._face.currentFrame === this._emotions[this._emotionState] - 1) {
            this._face.stop();
        }
    };


    private _generateGrid() {
        let w = this._width / this._config.gridSizeH,
            h = this._height / this._config.gridSizeV;

        this._cellsTotal = this._config.gridSizeH * this._config.gridSizeV;
        
        for (let i = 0, l = this._config.gridSizeV; i < l; i++) {
            for (let j = 0, l = this._config.gridSizeH; j < l; j++) {
                let cell = new PIXI.Container;
                cell.width = w;
                cell.height = h;
                cell.x = j * w;
                cell.y = i * h;
                cell.interactive = true;
                cell.buttonMode = true;
                                
                cell.addChild(this._drawRect(w, h, 0x000000, 0));
                this._cells.push(cell);
                this._grid.addChild(cell);
                
                cell.on(Level.CLICK, this._onCellClick, this);
                cell.on(Level.MOUSE_UP_OUTSIDE, this._onCellClick, this);
                cell.on(Level.TAP, this._onCellClick, this);
            }
        }
    };


    private _prepareBites() {
        if (!this._context.isIE()) {
            this._biteSource.addChild(this._drawRect());
        }
        this._bites = new PIXI.Container();
        let mask: PIXI.Graphics = this._drawRect();

        if (!this._context.isIE()) {
            this._bites.mask = mask;
        }
    };


    private _drawRect(w: number = this._width, h: number = this._height, color: number = 0xFFFFFF, alpha: number = 1) {
        let rect = new PIXI.Graphics;
        rect.beginFill(color, alpha);
        rect.drawRect(0, 0, w, h);
        rect.endFill();
        return rect;
    };


    @autobind
    private _onCellClick(e) {
        let factor = 1.5;
        var target = e.currentTarget as PIXI.Container;
        var cellIndex = this._cells.indexOf(target);
        this._cells.splice(cellIndex, 1);
        this._progress = 100 - (this._cells.length / this._cellsTotal * 100);
        this._changeEmotion();

        if (this._context.isIE()) {
            var bite = PIXI.Sprite.from(Assets.sprites['bite']._texture.baseTexture.imageUrl);
            bite.tint = GameAbstract.instance.config.bgColor;
        } else {
            var bite = PIXI.Sprite.from(Assets.sprites['bite']._texture.baseTexture.imageUrl);
            bite.tint = 0x000000;
        }

        // We set bite properties
        bite.pivot.x = bite.width / 2;
        bite.pivot.y = bite.height / 2;
        bite.width = target.width * factor;
        bite.height = target.height * factor;
        bite.rotation = NumberUtils.random(-20, 20) * (Math.PI / 180);
        bite.x = target.x + target.width / 2;
        bite.y = target.y + target.height / 2;
        
                
        this._bites.addChild(bite);
        
        // We add the bite to the container that serves as texture
        this._biteSource.addChild(this._bites);

        if (!this._context.isIE()) {
            // We generate a texture from that containers
            let texture = this._renderer.generateTexture(this._biteSource);
            
            // Let's update the character mask texture
            this._mask.texture = texture as PIXI.Texture;
            
            // And let's apply the mask (ugly, it's re-applied everytime we click on a cell)
            this._char.mask = this._mask;
        } else {
            this._level.addChildAt(this._biteSource, 1);
        }

        // explosion
        let explosion = new Explosion(target.x + target.width / 2, target.y + target.height / 2, this._config.particleColor);
        this._explosions.addChild(explosion.sprite);
        explosion.addListener(Explosion.DESTROYED, this._onExplosionDestroyed);
        

        // We remove the clickable element
        target.off(Level.CLICK, this._onCellClick);
        target.off(Level.MOUSE_UP_OUTSIDE, this._onCellClick);
        target.off(Level.TAP, this._onCellClick);
        this._grid.removeChild(target);
        target.destroy();
        
        this.dispatch({type: Level.HIT});

        this._checkForCompletion();
    };


    @autobind
    private _onExplosionDestroyed(e) {
        let target = e.target;
        this._explosions.removeChild(target);
        target = null;
    }


    public enable() {
        this._level.interactiveChildren = true;
    };


    public disable() {
        this._level.interactiveChildren = false;
    };


    @autobind
    private _checkForCompletion() {
        if (this._cells.length === 0) {
            this.dispatch({type: Level.COMPLETE});
            
            this.destroy();
        }
    };


    /*
     * destroy - destroy this instance
     * 
     */
    public destroy() {
        super.destroy();

        this._stage.removeChild(this._level);
        this._level.destroy();
    };


    /*
     * _resize - handle resize
     * 
     */
    protected _resize(e) {
        super._resize(e);

    };

}


export interface ILevelConfig {
    index: number;
    character: string;
    particleColor: number;
    faceOffsetX: number;
    faceOffsetY: number;
    posOffsetY?: number;
    gridSizeH: number;
    gridSizeV: number;
    ieShadow?: boolean;
}